import { useState, useContext, useRef, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Box, Checkbox, TextField, FormControlLabel, FormControl,
   Grid, Typography, Paper, RadioGroup, Radio, Chip } from "@mui/material";
import { deconstructMidasContainer, exportToExcel, getMessage } from "../../../global";
import { StyledTableRow, StyledTableBody, StyledTableCell, 
  UXDataTableWithoutBody } from "../../../components/UXDataTable";
import ContainerApi from '../../../api/LIMS/Container';
import ModalSimpleButton from "../../../components/Modal/ModalSimpleButton";
import LocationField from "../../../components/LocationField";
import { GlobalSecondaryButton, GlobalButton } from "../../styles";
import ProgressSpinnerPercent from "../../../components/ProgressSpinnerPercent";
import { LocationContext } from "../../../context/LocationContext";

const StyledTextField = styled(TextField)(() => ({
  width: "20rem"
}));

const SUCCESS = 'Success';
const FAILURE = 'Failure';
 
const BulkContainers = () => {
  const [listContainer, setListContainer] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  const [dataIsLoading, setDataIsLoading] = useState(false);
  const [scanOrder, setScanOrder] = useState(1);
  const [disableScan, setDisableScan] = useState(false);

  const targetInputRef = useRef(null);

  const [processingType, setProcessingType] = useState('none');
  const [discardLocation, setDiscardLocation] = useState('');
  const [cancelTests, setCancelTests] = useState(true);
  const [receiveLocation, setReceiveLocation] = useState(null);
  const [receiveSubLocation, setReceiveSubLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const [modalSimpleButton, setModalSimpleButton] = useState(false);
  const [modalSimpleButtonText, setModalSimpleButtonText] = useState('');
  const [modalSimpleTitle, setModalSimpleTitle] = useState('');
  const [modalSimpleText, setModalSimpleText] = useState('');

  const [currentProgress, setCurrentProgress] = useState(0);
  const [totalProgress, setTotalProgress] = useState(1);

  const { locations } = useContext(LocationContext);

  const totalItems = listContainer.length;
  const failedItems = listContainer.filter(c => c.error || c.processingStatus === FAILURE).length;
  const successItems = listContainer.filter(c => c.processingStatus === SUCCESS).length;
  const disableSubmit = totalItems === 0 || dataIsLoading || successItems > 0;

  const colHeaders = [
    { id: 'scanOrder', label: 'Scan Order' },
    { id: 'midasBarcode', label: 'MIDAS Barcode' },
    { id: 'midasNumber', label: 'MIDAS Number' },
    { id: 'containerNumber', label: 'Container Number' },
    { id: 'processingStatus', label: 'Processing Status' },
    { id: 'error', label: 'Error Message' }
  ];

  useEffect(() => {
    if (receiveLocation && receiveLocation.trim() !== '' && locations) {
      const activeLocations = locations?.filter(l => l.isActive) ?? [];
      setLocationError(
        activeLocations.some(result => result.locationName === receiveLocation) ?
          null : getMessage('INVALID_VALUE')
      );
    }
  }, [locations, receiveLocation]);

  async function discardContainers(barcodes) {
    return await ContainerApi.discardContainerBarcodes(barcodes, discardLocation, cancelTests);
  }

  async function receiveContainers(barcodes) {
    return await ContainerApi.receiveContainerBarcodes(barcodes, receiveLocation, receiveSubLocation);
  }

  const midasBarcodeLeave = (midasBarcode) => {
    midasBarcode = midasBarcode.trim();
    setTimeout(() => {targetInputRef.current.value = ''}, 10); 
    //midasInput.value = ''; // Clear the input field after processing
    if (midasBarcode !== '') {   
      const conObject = deconstructMidasContainer(midasBarcode);

      setListContainer([{ 
        scanOrder: scanOrder,
        midasBarcode: conObject.cleanMidasNumber,
        midasNumber: conObject.isValid ? conObject.formattedSampleName : '', 
        containerNumber: conObject.isValid ? conObject.containerNumber : '',
        error: conObject.errorMsg,
        processingStatus: conObject.isValid ? '' : FAILURE 
      },
        ...listContainer.filter(c => c.midasBarcode !== conObject.cleanMidasNumber)
      ]);

      setScanOrder(prev => prev + 1);
    }
  }

  const locationBarcodeLeave = (locationBarcode) => {
    locationBarcode = locationBarcode.trim(); 
    if (locationBarcode && locationBarcode !== '') {
      if (locationBarcode.includes('/')) {
        let locationArray = locationBarcode.split('/');
        setReceiveLocation(locationArray[0]);
        setReceiveSubLocation(locationArray[1]);
      } else {
        setReceiveLocation(locationBarcode);
      }
    }
  }

  const handleMidasBarcodeKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      midasBarcodeLeave(event.target.value)
    }
  }

  const handleLocationBarcodeKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      locationBarcodeLeave(event.target.value)
    }
  }

  function processContainers(asyncProcssingFunc, batchSize = 40) {
    const allBarcodes = listContainer.map((item) => item.midasBarcode)
    if (allBarcodes.length === 0) {
      return;
    }

    const intialData = listContainer.map((item) => {
      return {
        ...item,
        processingStatus: '',
        error: ''
      };
    });
    setListContainer(intialData);

    setDataIsLoading(true);
    setCurrentProgress(0);
    const totalProgress = allBarcodes.length;
    setTotalProgress(totalProgress);

    async function processBatch(startIndex, updatedData = []) {
      const barcodesBatch = allBarcodes.slice(startIndex, startIndex + batchSize) ?? [];
      if (barcodesBatch.length === 0) {
        finalizeProcessing(updatedData, '');
        return;
      }

      asyncProcssingFunc(barcodesBatch).then((res) => {  
        if (res && res.message === SUCCESS) {
          const processedBatchResults = res.result;
          updatedData = updatedData.map((item) => {
            const resultItem = processedBatchResults.find(c => c.midasBarcode === item.midasBarcode);
            return {
              ...item,
              processingStatus: resultItem ? resultItem.isSuccess ? SUCCESS : FAILURE : item.processingStatus,
              error: resultItem ? resultItem.errorMessage : item.error
            };
          });

          setListContainer(updatedData);

          startIndex += batchSize;
          setCurrentProgress(startIndex > totalProgress ? totalProgress : startIndex);
          return processBatch(startIndex, updatedData);
        }
        else {
          const message = res.message && res.message !== '' ? res.message : 'Something went wrong, please try again later.'
          finalizeProcessing(updatedData, message);
          return;
        }
      });
    }

    processBatch(0, intialData).catch((error) => {
      setDataIsLoading(false);
      openModalSimpleButton('Fail', error ? error : 'Something went wrong, please try again later.');
    });
  }

  function finalizeProcessing(updatedData = [], errorMessage='') {
    setDataIsLoading(false);

    updatedData = updatedData.map((item) => {
      return {
        ...item,
        processingStatus: item.processingStatus ? item.processingStatus : FAILURE,
        error: item.processingStatus ? item.error : 'Not processed'
      };
    });
    setListContainer(updatedData);

    if (errorMessage) {
      openModalSimpleButton('Fail', errorMessage, 'Ok');
    }
    else {
      const successCount = updatedData.filter(item => item.processingStatus === SUCCESS).length;
      const failedCount = updatedData.filter(item => item.processingStatus === FAILURE).length;
      const summaryType = processingType === 'discard' ? 'Discarded' : 'Received';
      openModalSimpleButton('Completed', `Summary of ${summaryType} Containers: ${successCount} Succeeded, ${failedCount} Failed`, 'Ok');
    }
  }

  function handleDiscardSubmit() {
    setDisableScan(true);
    processContainers(discardContainers);  
  }

  function handleReceiveSubmit() {
    if (locationError) return;

    if (!receiveLocation || receiveLocation.trim() === '') {
      setLocationError(getMessage('REQUIRED_FIELD'));
      return;
    }

    setDisableScan(true);
    processContainers(receiveContainers);  
  }

  function clearContainers(clearOnlySuccess = false) {
    const updatedData = clearOnlySuccess ? listContainer.filter(item => item.processingStatus !== SUCCESS) : [];
    setListContainer(updatedData);

    if (updatedData.length === 0) {
      setScanOrder(1);
      setDisableScan(false);
    }    
  }

  const changeLocation = (value, setValue) => setValue(value);

  function openModalSimpleButton(title, text, buttonText) {
    setModalSimpleButtonText(buttonText)
    setModalSimpleButton(true);
    setModalSimpleTitle(title);
    setModalSimpleText(text);
  }

  function closeModalSimpleButton() {
    setModalSimpleButton(false);
  } 

  const truncateString = (str, length=18) => {
    return str.length > length ? str.slice(0, length) + '...' : str;
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function determineStartArray(arrayContainers) {
    if (arrayContainers) {
      return stableSort(arrayContainers, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    } else {
      return []
    }
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const SubmitHelperText = () => {
    let helperText = '';
    if (dataIsLoading){
      helperText = 'Processing...';
    }
    else if (disableScan && failedItems > 0){
      helperText = successItems > 0 ?
       'To resubmit failures, clear successful containers'
       :  'Resubmit failures';
    }
    return <Typography 
        variant="caption" 
        color="textSecondary"
        style={{ marginTop: '5px', marginLeft: '10px' }}
        >
        {helperText}
      </Typography>
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '0px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%'
      }}>
        <Box style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
          <StyledTextField
            label="Scan MIDAS barcode #"
            variant="outlined"
            size="small"
            disabled={disableScan || dataIsLoading}
            inputRef={targetInputRef}
            onKeyDown={e => handleMidasBarcodeKeyPress(e)}
            onBlur={(e) => midasBarcodeLeave(e.target.value)}
          />
          {!dataIsLoading && disableScan && 
            <Typography 
              variant="caption"
              color="textSecondary"
              align="Center"
              style={{ marginTop: '5px' }}
              >
              Clear all containers to scan more!
            </Typography>
          }
        </Box>

        <Box style={{ display: 'inline' }}>

          <GlobalSecondaryButton
            variant='contained'
            disabled={totalItems===0 || dataIsLoading}
            style={{ marginRight: '10px' }}
            onClick={(e) =>
              exportToExcel(
                listContainer.map(item =>
                  Object.fromEntries(colHeaders.map(col => [col.label, item[col.id]]))
                ),
                "Bulk Containers",
              )
            }
          >Export</GlobalSecondaryButton>

          <GlobalSecondaryButton
            variant='contained'
            onClick={() => {
              clearContainers(true);
            }}
            style={{ marginRight: '10px' }}
            disabled={successItems===0 || dataIsLoading}
          >Clear Successful Containers</GlobalSecondaryButton>

          <GlobalSecondaryButton
            variant='contained'
            onClick={() => {
              clearContainers();
            }}
            disabled={totalItems===0 || dataIsLoading}
          >Clear All</GlobalSecondaryButton>
        </Box>
      </div>

      <Grid container spacing={3}>
        {/*Left section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} style={{ padding: '10px' }}>
            <UXDataTableWithoutBody
              tableWidth='100%'
              cols={colHeaders}
              blankFirstHeader={false}
              tableId="simpleResultTable"
              rowLength={listContainer.length}
              enablePaging={true}
              rowsPerPage={rowsPerPage}
              page={page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              noDataFoundMessage={'There are no scanned MIDAS numbers.'}
              enableAddIcon={false}
              addFunction={null}
              addToolTipText={null}
              enableCheckbox={false}
              enableSorteable={true}
              order={order}
              orderBy={orderBy}
              handleRequestSort={handleRequestSort}
            >
              <StyledTableBody key={"myTablebody"}>
                {listContainer?.length > 0 && dataIsLoading && <StyledTableRow
                  hover
                  key={`custom-row-loading`}>
                  <StyledTableCell colSpan={9} style={{ textAlign: 'center' }}>
                    <ProgressSpinnerPercent 
                      style={{textTransform: "none", marginLeft:"15px"}}
                      value={Math.round((currentProgress / totalProgress) * 100)}
                      size={60}
                    />
                  </StyledTableCell>
                </StyledTableRow>}
                {determineStartArray(listContainer).map((container, rowIndex) => {
                  return (
                    <StyledTableRow
                      hover
                      key={`custom-row-${rowIndex}`}>
                      <StyledTableCell style={{ width: '30px' }}>{container.scanOrder}</StyledTableCell>    
                      <StyledTableCell style={{ width: '200px' }}>{truncateString(container.midasBarcode) }</StyledTableCell>   
                      <StyledTableCell style={{ width: '150px' }}>{container.midasNumber}</StyledTableCell>
                      <StyledTableCell style={{ width: '30px' }}>{container.containerNumber}</StyledTableCell>  
                      <StyledTableCell style={{ width: '50px' }}>
                        {container.processingStatus && <Chip
                            label={container.processingStatus}
                            color={container.processingStatus === SUCCESS ? 'success' : 'error'}
                          />
                        }
                      </StyledTableCell>
                      <StyledTableCell style={{ width: '300px' }}>{container.error}</StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </StyledTableBody>
            </UXDataTableWithoutBody>
          </Paper>
        </Grid>

        {/*Right section */}
        <Grid item xs={12} md={4}>
          {/*Summary section*/}
          <Paper elevation={3} style={{ padding: '1rem' }}>
            <Grid container spacing={1} alignItems="center">
              {!disableScan && <>
                <Grid item xs={4}>
                  <Typography>Last scanned:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography 
                    variant="h4" 
                    color={failedItems > 0 && listContainer[0].processingStatus === FAILURE ? 'error' : 'textPrimary'}
                    align="right"
                  >
                    {totalItems === 0 ? 'None' :
                      listContainer[0].processingStatus === FAILURE ? 
                        truncateString(listContainer[0].midasBarcode) : 
                        `${listContainer[0].midasNumber}-${listContainer[0].containerNumber}`
                    }
                  </Typography>
                </Grid>
              </>}

              <Grid item xs={4}>
                <Typography>Total containers:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="h4" align="right">
                  {totalItems}
                </Typography>
              </Grid>

              {failedItems > 0 && <>
                <Grid item xs={4}>
                  <Typography>Failed containers:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography
                    variant="h4"
                    align="right"
                    color={failedItems > 0 ? 'error' : 'textPrimary'}                 
                  >
                    {failedItems}
                  </Typography>
                </Grid>
              </>}

              {successItems > 0 && <>
                <Grid item xs={4}>
                  <Typography>Successful containers:</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography
                    variant="h4"
                    align="right"
                    color={successItems > 0 ? 'green' : 'textPrimary'}
                  >
                    {successItems}
                  </Typography>
                </Grid>
              </>}
            </Grid>
          </Paper>

          <FormControl disabled={disableScan} style={{ marginTop: '30px' }} fullWidth>
            <RadioGroup
              row
              name="row-radio-buttons-group"
              value={processingType}
              onChange={(e) => setProcessingType(e.target.value)}
            >
              <FormControlLabel 
                value="discard" 
                control={<Radio />} 
                label={<div
                  style={{ fontWeight: 'bold' }}>
                  Discard Containers
                </div>} 
              />
              <FormControlLabel 
                value="receive" 
                control={<Radio />} 
                label={<div
                  style={{ fontWeight: 'bold' }}>
                  Receive Containers
                </div>} 
              />
            </RadioGroup>
          </FormControl>

          {processingType === 'discard' && <>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '30px', marginBottom: '10px' }}>
              <LocationField
                isDisabled={dataIsLoading}
                value={discardLocation === '' ? null : discardLocation}
                handleChange={(value) => changeLocation(value, setDiscardLocation)}
                label="Discard Location"
              />

              <FormControlLabel control={
                <Checkbox checked={cancelTests}
                  onChange={() => setCancelTests(!cancelTests)}
                  disabled={dataIsLoading}
                ></Checkbox>} label={"Cancel Backlogged/ Pending tests"} />

              <div>
                <GlobalButton
                  variant='contained'
                  style={{ width: "11rem" }}
                  type="submit"
                  onClick={handleDiscardSubmit}
                  disabled={disableSubmit}
                >Discard Containers</GlobalButton>
                <SubmitHelperText />
              </div>
            </Box>
          </>}

          {processingType === 'receive' && <div 
            style={{ display: 'flex', gap: '5px', flexDirection: 'column', marginBottom: "5rem" }}>

            <Box style={{ display: 'flex', flexDirection: 'column', marginTop: '20px', marginBottom: '20px', gap: '5px' }}>
              <Typography>New Location</Typography>
              <StyledTextField
                label="Scan Shelf Label"
                variant="outlined"
                size="small"
                isDisabled={dataIsLoading}
                onKeyDown={e => handleLocationBarcodeKeyPress(e)}
                onBlur={(e) => locationBarcodeLeave(e.target.value)}
              /> 
            </Box>
            
            <Typography>Or</Typography>
            <Box style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
              <LocationField
                value={receiveLocation === '' ? null : receiveLocation}
                handleChange={(value) => changeLocation(value, setReceiveLocation)}
                hasErrors={locationError}
                label="Receive Location"
                isDisabled={dataIsLoading}
                helperText={Boolean(locationError) ? locationError : ''}
              />
              <StyledTextField
                label="Sublocation"
                variant="outlined"
                size="small"
                isDisabled={dataIsLoading}
                value={receiveSubLocation ?? ''}
                onChange={e => setReceiveSubLocation(e.target.value)}
              />
            </Box>
            
            <div style={{marginTop: '30px', marginBottom: '30px'}}>
              <GlobalButton sx={{ width: "11rem" }}
                variant='contained'
                type="submit"
                onClick={handleReceiveSubmit}
                disabled={disableSubmit}
              >Receive Containers</GlobalButton>
              <SubmitHelperText />
            </div>

          </div>}
        </Grid>
      </Grid>

      <ModalSimpleButton 
        title={modalSimpleTitle} 
        buttonText={modalSimpleButtonText} 
        buttonAction={closeModalSimpleButton} 
        open={modalSimpleButton} 
        setOpen={setModalSimpleButton}>
        <label>
          {modalSimpleText}
        </label>
      </ModalSimpleButton>
    </Box>
  );
};

export default BulkContainers;
